import type { ReactNode } from "react";

import { cn } from "~/shared/lib/utils";

import { SECTION_V5 } from "./design-tokens";

export interface SectionTabsToolbarProps<T extends string> {
    /** Ordered list of tab values to render. */
    tabs: readonly T[];
    /** Currently active tab. */
    activeTab: T;
    /** Fired with the clicked tab (skipped for disabled tabs). */
    onTabChange: (tab: T) => void;
    /** Visible label for a tab (usually a translated string). */
    getLabel: (tab: T) => ReactNode;
    /** `aria-label` for the `role="tablist"` container. */
    ariaLabel: string;
    /**
     * Wrap the tablist in the `SECTION_V5.toolbarRow` row. Default `true`.
     * Set `false` when a parent already provides the toolbar row (e.g. folders,
     * where `FoldersView` owns the row and gutter).
     */
    withRow?: boolean;
    /** Optional per-tab disabled predicate (e.g. «coming soon» stubs). */
    isDisabled?: (tab: T) => boolean;
    /** Optional `title` for a disabled tab (tooltip). */
    getDisabledTitle?: (tab: T) => string | undefined;
}

/**
 * Generic underline-style tabs toolbar shared by the section list headers
 * (projects / chats / assets / folders). Markup is identical across callers;
 * they vary only in tab set, labels, aria-label, the optional toolbar-row
 * wrapper and optional disabled-tab support.
 */
export function SectionTabsToolbar<T extends string>({
    tabs,
    activeTab,
    onTabChange,
    getLabel,
    ariaLabel,
    withRow = true,
    isDisabled,
    getDisabledTitle,
}: SectionTabsToolbarProps<T>) {
    const tablist = (
        <div
            role="tablist"
            aria-label={ariaLabel}
            className="flex min-w-0 flex-1 [scrollbar-width:none] items-center gap-6 overflow-x-auto [&::-webkit-scrollbar]:hidden"
        >
            {tabs.map((tabValue) => {
                const active = tabValue === activeTab;
                const disabled = isDisabled?.(tabValue) ?? false;
                return (
                    <button
                        type="button"
                        role="tab"
                        aria-selected={active}
                        aria-disabled={disabled || undefined}
                        disabled={disabled || undefined}
                        title={disabled ? getDisabledTitle?.(tabValue) : undefined}
                        key={tabValue}
                        onClick={() => !disabled && onTabChange(tabValue)}
                        className={cn(
                            "inline-flex h-11 shrink-0 flex-col items-center transition-colors md:h-10",
                            disabled
                                ? "cursor-not-allowed text-muted-foreground/50"
                                : active
                                  ? "text-foreground"
                                  : "text-muted-foreground hover:text-foreground",
                        )}
                    >
                        <span className="flex flex-1 items-center text-sm font-medium whitespace-nowrap">
                            {getLabel(tabValue)}
                        </span>
                        <span
                            className={cn(
                                "h-0.5 w-full rounded-t-full transition-colors",
                                active && !disabled ? "bg-primary" : "bg-transparent",
                            )}
                        />
                    </button>
                );
            })}
        </div>
    );

    if (!withRow) return tablist;
    return <div className={SECTION_V5.toolbarRow}>{tablist}</div>;
}
